<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <RfCustomGoalInteractionSelector
        v-if="hasCustomGoal && experiment && !isInvisible"
        ref="interaction_selector"
        :key="experiment.id"
        :model="model"
      />
    </div>
    <template v-if="hasTableData">
      <RfTable
        :tableData="{ totalCount: tableRows.length + 1 }"
        class="[&_tbody_tr]:border-strokes [&_tbody_tr]:border-t [&_tbody_tr]:border-solid"
        hideTotal
        noIntersection
      >
        <template #thead>
          <RfTableHeader
            :isInvisible="isInvisible"
            :hasCustomGoal="hasCustomGoal"
            :accept2Enabled="accept2Enabled"
            :shouldShowCTR="shouldShowCTR"
            :showImpressions="showImpressions"
            :interactionsTitle="interactionsTitle"
            :ctrTitle="ctrTitle"
            :customGoalHeader="isInvisible ? 'Conversions' : 'Goal Completion'"
          >
            <template #additional-headers>
              <template v-if="mode === 'completed'">
                <th class="min-w-24 text-right whitespace-nowrap">{{ goalsTitle }} (additional)</th>
                <th class="min-w-28 text-right whitespace-nowrap">Conversion Rate (additional)</th>
                <th class="min-w-28 text-right whitespace-nowrap">
                  Statistical Significance* (additional)
                </th>
                <th class="min-w-10">Winner</th>
              </template>
              <template v-else>
                <th class="min-w-28 text-right whitespace-nowrap">
                  {{ customGoalName }} Conversion Rate (additional)
                </th>
                <th class="min-w-28 text-right whitespace-nowrap">
                  Statistical Significance* (additional)
                </th>
              </template>
            </template>
            <template #trailing-headers>
              <th v-if="mode === 'active'" class="min-w-10"></th>
            </template>
          </RfTableHeader>
        </template>

        <template #tbody>
          <RfTableRow
            v-for="(row, index) in tableRows"
            :key="index"
            :row="row"
            :isInvisible="isInvisible"
            :hasCustomGoal="hasCustomGoal"
            :accept2Enabled="accept2Enabled"
            :shouldShowCTR="shouldShowCTR"
            :showImpressions="showImpressions"
            :lastUpdatedText="experiment && experiment.stats_last_updated_text"
          >
            <template #variation-cell="{ row: r }">
              {{ mode === "completed" ? r.rawItem.name : r.rawItem.variantName }}
            </template>

            <template #row-additional="{ row: r }">
              <td
                v-if="mode === 'completed'"
                class="rf-row--metric-col px-4! py-3! text-right whitespace-nowrap"
              >
                {{ r.goals2 }}
              </td>

              <td class="rf-row--metric-col px-4! py-3! text-right whitespace-nowrap">
                {{ r.conversionRate2 }}
              </td>
              <td class="rf-row--metric-col px-4! py-3! text-right whitespace-nowrap">
                <span :style="r.confidence2.style">{{ r.confidence2.confidence }}</span>
              </td>
            </template>

            <template #row-trailing="{ row: r }">
              <td v-if="mode === 'completed'" class="px-4! py-3! text-center">
                <v-icon v-if="r.rawItem.is_winner" size="30" color="#dcc214">
                  md:emoji_events
                </v-icon>
              </td>
              <td v-else class="px-4! py-3! text-center">
                <RfButtonNew
                  v-if="r.rawItem.is_visible"
                  text="Use this"
                  size="small"
                  class="use-this-variant"
                  type="secondary"
                  :disabled="userStore.isDisabledWithMessage"
                  @click="confirmModal(r.rawItem)"
                />
              </td>
            </template>
          </RfTableRow>

          <tr class="text-body text-black-2 font-medium *:px-4! *:py-3!">
            <td>
              <span v-if="mode === 'completed'">Total</span>
              <button
                v-else
                class="rf-exp-graph-variant"
                @click="$emit('update:pieActionGroupId', null)"
              >
                Total
              </button>
            </td>
            <template v-if="!isInvisible">
              <td class="text-right whitespace-nowrap">{{ totalUsersText }}</td>
              <td v-if="showImpressions" class="text-right whitespace-nowrap">
                {{ totalImpressionsText }}
              </td>
            </template>
            <td v-if="isInvisible" class="text-right whitespace-nowrap">
              {{ totalUsersText }}
            </td>
            <td class="text-right whitespace-nowrap">{{ totalGoalsText }}</td>
            <td v-if="!isInvisible && shouldShowCTR" class="text-right whitespace-nowrap">
              <template v-if="mode === 'active'">{{ displayCTR }}</template>
            </td>
            <td v-if="hasCustomGoal" class="text-right whitespace-nowrap">
              {{ totalCustomGoalsText }}
            </td>
            <td></td>
            <td></td>
            <td></td>
            <template v-if="accept2Enabled && !hasCustomGoal">
              <td class="text-right whitespace-nowrap">{{ totalGoals2Text }}</td>
              <td v-if="mode === 'active'" class="text-right whitespace-nowrap">
                {{ totalCTR2 }}
              </td>
              <td v-else></td>
              <td class="text-right whitespace-nowrap">{{ totalGoals2Text }}</td>
              <td></td>
            </template>
            <td></td>
          </tr>
        </template>
      </RfTable>

      <div class="text-body text-beige-30 pl-2">
        *Statistical significance calculations are for informational and illustrative purposes only.
        Raw data is available to you for more advanced analysis.
      </div>
    </template>

    <RfDialog
      v-if="mode === 'active'"
      ref="confirmDialogRef"
      :title="`Use ${variantItem.variantName} confirmation`"
      width="800"
    >
      You've selected {{ variantItem.variantName }} as the winner of this experiment.<br />Are you
      sure you want to use this variation? This ends the experiment.
      <template #footer>
        <RfButtonNew type="quaternary" @click="closeConfirmDialog">
          <span class="px-4">Cancel</span>
        </RfButtonNew>
        <RfButtonNew type="main" @click="onWinner(variantItem.variant.id, false)">
          <span class="px-4">Use {{ variantItem.variantName }}</span>
        </RfButtonNew>
      </template>
    </RfDialog>
  </div>
</template>

<script>
import RfCustomGoalInteractionSelector from "@/components/RfCommonCards/RfCustomGoalInteractionSelector.vue";
import CustomGoalInteractionMixin from "@/utils/CustomGoalInteractionMixin";
import { percentage } from "@/utils/metricsHelpers";
import { isAccept2Enabled, isInvisible } from "@/utils/prompts/promptHelpers";
import ExperimentCsvExportMixin from "@/utils/ExperimentCsvExportMixin";
import StringUtils from "@/utils/StringUtils";
import { toLocaleNumberString } from "@/utils/stringHelpers";
import { interactionDenominators } from "@/utils/constants/PromoInteractionConstants";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useUserStore } from "@/pinia/userStore";
import { useCustomFields } from "@/utils/composables/useCustomFields";
import { cloneDeep } from "lodash-es";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfTableHeader from "./RfTableHeader.vue";
import RfTableRow from "./RfTableRow.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import { computed } from "vue";

export default {
  name: "RfExperimentUnifiedTable",
  components: {
    RfCustomGoalInteractionSelector,
    RfTable,
    RfTableHeader,
    RfTableRow,
    RfButtonNew,
    RfDialog,
  },
  mixins: [CustomGoalInteractionMixin, ExperimentCsvExportMixin],
  props: {
    mode: { type: String, default: "active" },
    path: { type: Object, default: () => ({}) },
    experiment: { type: Object, default: null },
    metrics: { type: Object, default: () => ({}) },
    metricsKey: { type: String, default: "since_start_date" },
    timePeriod: { type: Array, default: () => [] },
    pieActionGroupId: { type: String, default: null },
  },
  emits: ["update:pieActionGroupId", "showVariantDetails"],
  setup: props => ({
    isInvisible: computed(() => isInvisible(props.path)),
    formatStatsFixed: StringUtils.formatStatFixed,
    experimentsStore: useExperimentsStore(),
    promptStore: usePromptStore(),
    userStore: useUserStore(),
    appCustomFields: useCustomFields().appCustomFields,
  }),
  data() {
    return {
      variantItem: {},
    };
  },
  computed: {
    model() {
      return this.path;
    },
    showImpressions() {
      return this.experimentsStore.customGoalInteraction !== "seen";
    },
    // ── Completed mode ──────────────────────────────────────────────────────
    variationsDisplay() {
      if (!this.experiment?.variations) return [];
      return this.experiment.variations.filter(item => item.traffic_percent > 0);
    },
    // ── Active mode ─────────────────────────────────────────────────────────
    variations() {
      if (!this.experiment?.variations) return [];
      return this.experiment.variations.filter(item => item.is_visible || item.is_control);
    },
    filteredStats() {
      if (!this.metrics[this.metricsKey]?.data) return [];
      const result = [];
      this.variations.forEach(variation => {
        if (variation.traffic_percent > 0) {
          const data = this.metrics[this.metricsKey].data.find(item => item.id === variation.id);
          if (data) result.push(data);
        }
      });
      return result;
    },
    runningStat() {
      const colors = [
        "color:#4D96E6",
        "color:#9DC9F7",
        "color:#234569",
        "color:#245897",
        "color:#CBE3FC",
        "color:#63CCFF",
      ];
      return this.filteredStats.map(item => ({
        variant: item,
        variantName: `${item.name}`,
        is_visible: item.is_visible,
        is_control: item.is_control,
        is_baseline: item.is_baseline,
        variantColor: colors.shift(),
        ctr: percentage(item.goals.uniques, item.impressions.count),
        conversion_rate: this.conversionRate(item),
        goals: item.goals.uniques,
        custom_goals: (item.custom_goals && item.custom_goals.uniques) || 0,
        impressions: item.impressions.count,
        uimpressions: item.uimpressions.uniques,
        uholdouts: item.uholdouts.uniques,
        goals2: item.goals2.uniques,
        custom_goals_b2: (item.custom_goals_b2 && item.custom_goals_b2.uniques) || 0,
        ctr2: percentage(item.goals2.uniques, item.impressions.count),
        conversion_rate2: this.conversionRate2(item),
        custom_goals_accepted:
          (item.custom_goals_accepted && item.custom_goals_accepted.uniques) || 0,
        custom_goals_accepted2:
          (item.custom_goals_accepted2 && item.custom_goals_accepted2.uniques) || 0,
        custom_goals_declined:
          (item.custom_goals_declined && item.custom_goals_declined.uniques) || 0,
        custom_goals_dismissed:
          (item.custom_goals_dismissed && item.custom_goals_dismissed.uniques) || 0,
        custom_goals_seen: (item.custom_goals_seen && item.custom_goals_seen.uniques) || 0,
        declines: (item.declines && item.declines.uniques) || 0,
        dismissals: (item.dismissals && item.dismissals.uniques) || 0,
        custom_goals_holdout: (item.custom_goals_holdout && item.custom_goals_holdout.uniques) || 0,
      }));
    },
    // ── Table data guard ────────────────────────────────────────────────────
    hasTableData() {
      if (this.mode === "completed") return this.variationsDisplay.length > 0;
      return !!this.metrics[this.metricsKey]?.data?.length;
    },
    // ── Shared flags ────────────────────────────────────────────────────────
    accept2Enabled() {
      return !!this.experiment?.variations?.find(isAccept2Enabled);
    },
    usersKey() {
      return this.isInvisible ? "users" : "uimpressions";
    },
    holdoutVariant() {
      return this.experiment?.variations?.find(item => item.is_control) ?? null;
    },
    holdoutTraffic() {
      return this.holdoutVariant?.traffic_percent ?? 0;
    },
    // ── Rows ────────────────────────────────────────────────────────────────
    tableRows() {
      if (this.mode === "completed") {
        return this.variationsDisplay.map(item => ({
          rawItem: item,
          users: this.formatUsers(item),
          impressions: this.formatImpressions(item),
          goals: this.formatGoals(item),
          ctr: this.formatCTR(item),
          customGoals: this.formatCustomGoals(item),
          conversionRate: this.formatConversion(item),
          confidence: this.confidenceIndicator(item),
          goals2: this.formatGoals2(item),
          ctr2: this.formatCTR2(item),
          conversionRate2: this.formatConversion2(item),
          confidence2: this.confidenceIndicator(item, true),
          rowClass: item.is_winner ? "winner-row" : "default-row",
        }));
      }
      return this.runningStat.map(item => ({
        rawItem: item,
        users: this.formatUsers(item),
        impressions: this.formatImpressions(item),
        goals: this.formatGoals(item),
        ctr: this.formatCTR(item),
        customGoals: this.formatCustomGoals(item),
        conversionRate: this.formatConversion(item),
        confidence: this.confidenceIndicator(item),
        goals2: this.formatGoals2(item),
        ctr2: this.formatCTR2(item),
        conversionRate2: this.formatConversion2(item),
        confidence2: this.confidenceIndicator(item, true),
      }));
    },
    // ── Totals ───────────────────────────────────────────────────────────────
    totalUsersText() {
      if (this.mode === "completed") {
        const count = this.experiment.variations.reduce(
          (accum, item) => accum + item.stats[item.is_control ? "uholdouts" : this.usersKey],
          0,
        );
        return count >= 0 ? this.formatStatsLocale(count) : "";
      }
      const holdouts = this.holdoutStatsItem.uholdouts || 0;
      return this.formatStatsLocale(this.sumStats("uimpressions") + holdouts);
    },
    totalImpressionsText() {
      if (this.mode === "completed") {
        const count = this.experiment.variations
          .filter(item => item.is_visible || !item.is_control)
          .reduce((accum, item) => accum + item.stats.impressions, 0);
        return count >= 0 ? this.formatStatsLocale(count) : this.totalVisitorsData("impressions");
      }
      return this.formatStatsLocale(this.sumStats("impressions"));
    },
    totalGoalsText() {
      if (this.mode === "completed") {
        const statType =
          interactionDenominators[this.experimentsStore.customGoalInteraction] || "goals";
        return this.totalCompletedData(statType, true);
      }
      return this.formatStatsLocale(
        this.totalGoals + ((this.isInvisible && this.holdoutStatsItem?.uholdouts) || 0),
      );
    },
    totalCustomGoalsText() {
      if (this.mode === "completed") {
        const holdoutExtra =
          (this.holdoutVariant && this.holdoutVariant.stats?.custom_goals_holdout) || 0;
        return this.totalCompletedData(
          `custom_goals_${this.experimentsStore.customGoalInteraction}`,
          true,
          holdoutExtra,
        );
      }
      return this.formatStatsLocale(this.totalCustomGoals);
    },
    totalGoals2Text() {
      if (this.mode === "completed") return this.totalCompletedData("goals2");
      return this.formatStatsLocale(this.sumStats("goals2"));
    },
    // Active-only totals (also used by CustomGoalInteractionMixin for displayCTR etc.)
    totalImpressions() {
      return this.sumStats("impressions");
    },
    totalUsers() {
      return this.sumStats("uimpressions") + (this.holdoutStatsItem?.uholdouts || 0);
    },
    totalGoals() {
      if (!this.hasCustomGoal) return this.sumStats("goals");
      const statType = interactionDenominators[this.experimentsStore.customGoalInteraction];
      return this.sumStats(statType);
    },
    totalCustomGoals() {
      const statType = `custom_goals_${this.experimentsStore.customGoalInteraction}`;
      return this.sumStats(statType, true) + (this.holdoutStatsItem?.custom_goals_holdout || 0);
    },
    holdoutStatsItem() {
      return this.runningStat?.find(item => item.is_control) || {};
    },
    totalCTR2() {
      return this.totalImpressions
        ? percentage(this.sumStats("goals2"), this.totalImpressions, true)
        : "N/A";
    },
    goalVal() {
      return !this.hasCustomGoal
        ? "goals"
        : `custom_goals_${this.experimentsStore.customGoalInteraction}`;
    },
  },
  methods: {
    exportToCSV(filterDataKeys) {
      const items = this.mode === "completed" ? this.variationsDisplay : this.runningStat;
      this.exportExperimentsToCsv(items, filterDataKeys);
    },
    totalStatByKey(item, key) {
      if (this.mode === "completed") return item?.stats?.[key];
      const variation = this.experiment?.variations?.find(v => v.id === item?.variant?.id);
      return variation?.stats?.[key];
    },
    // ── Completed helpers ───────────────────────────────────────────────────
    totalCompletedData(field, skipHoldout, additional) {
      let count = this.experiment.variations.reduce((accum, item) => {
        if (skipHoldout && item.is_control) return accum;
        return accum + item.stats[field];
      }, 0);
      if (additional) count += additional;
      return count >= 0 ? this.formatStatsLocale(count) : undefined;
    },
    totalVisitorsData(key) {
      const count = this.experiment.variations.reduce((accum, item) => accum + item.stats[key], 0);
      return count >= 0 ? this.formatStatsLocale(count) : "N/A";
    },
    // ── Active helpers ──────────────────────────────────────────────────────
    sumStats(key, includeControl) {
      if (!this.runningStat) return 0;
      return this.runningStat.reduce((accum, item) => {
        if (item.is_control && !includeControl) return accum;
        return accum + (item[key] || 0);
      }, 0);
    },
    conversionRate(item) {
      const numerator = this.hasCustomGoal
        ? (item.custom_goals && item.custom_goals.uniques) || 0
        : item.goals.uniques;
      return percentage(numerator, this.conversionDenominator(item));
    },
    conversionRate2(item) {
      const numerator = this.hasCustomGoal
        ? (item.custom_goals_b2 && item.custom_goals_b2.uniques) || 0
        : item.goals2.uniques;
      return percentage(numerator, this.conversionDenominator(item));
    },
    conversionDenominator(item) {
      if (this.hasCustomGoal && item.is_control) return item.uholdouts.uniques;
      return item.uimpressions.uniques;
    },
    // ── Format methods ───────────────────────────────────────────────────────
    formatStatsLocale(stat) {
      return stat ? toLocaleNumberString(stat) : "N/A";
    },
    formatUsers(item) {
      if (this.mode === "completed") {
        const stats = item.is_control ? item.stats.uholdouts : item.stats[this.usersKey];
        return this.formatStatsLocale(stats);
      }
      return this.formatStatsLocale(item.is_control ? item.uholdouts : item.uimpressions);
    },
    formatImpressions(item) {
      if (item.is_control) return "-";
      return this.formatStatsLocale(
        this.mode === "completed" ? item.stats.impressions : item.impressions,
      );
    },
    formatGoals(item) {
      if (item.is_control) {
        return this.isInvisible && item.uholdouts ? toLocaleNumberString(item.uholdouts) : "-";
      }
      if (this.mode === "completed") {
        const statType = interactionDenominators[this.experimentsStore.customGoalInteraction];
        return this.formatStatsLocale(
          !this.hasCustomGoal ? item.stats.goals : item.stats[statType],
        );
      }
      const stats = !this.hasCustomGoal
        ? item.goals
        : item[interactionDenominators[this.experimentsStore.customGoalInteraction]];
      return this.formatStatsLocale(stats);
    },
    formatGoals2(item) {
      if (item.is_control) return "-";
      return this.formatStatsLocale(this.mode === "completed" ? item.stats.goals2 : item.goals2);
    },
    formatCustomGoals(item) {
      const key = `custom_goals_${this.experimentsStore.customGoalInteraction}`;
      if (this.mode === "completed") {
        return this.formatStatsLocale(
          item.is_control ? item.stats.custom_goals_holdout : item.stats[key],
        );
      }
      return this.formatStatsLocale(item.is_control ? item.custom_goals_holdout : item[key]);
    },
    formatConversion(item) {
      if (item.is_control && !this.hasCustomGoal) return "-";
      if (this.mode === "completed") {
        if (!this.hasCustomGoal)
          return this.formatStatsFixed(this.completedConversionPct(item, false));
        const numerator = item.is_control
          ? item.stats.custom_goals_holdout || 0
          : item.stats[`custom_goals_${this.experimentsStore.customGoalInteraction}`] || 0;
        const denominator = item.stats[item.is_control ? "uholdouts" : this.usersKey] || 0;
        return this.formatStatsFixed(percentage(numerator, denominator));
      }
      if (!this.hasCustomGoal) return this.formatStatsFixed(item.conversion_rate);
      const numerator = this.getActiveCustomGoals(item) || 0;
      const denominator = item[item.is_control ? "uholdouts" : "uimpressions"] || 0;
      return this.formatStatsFixed(percentage(numerator, denominator));
    },
    formatConversion2(item) {
      if (item.is_control && !this.hasCustomGoal) return "-";
      if (this.mode === "completed")
        return this.formatStatsFixed(this.completedConversionPct(item, true));
      return this.formatStatsFixed(item.conversion_rate2);
    },
    formatCTR(item) {
      if (item.is_control) return "-";
      if (this.experimentsStore.customGoalInteraction === "accepted2") return this.formatCTR2(item);
      if (this.mode === "completed")
        return this.formatStatsFixed(percentage(item.stats.goals, item.stats.impressions));
      return this.formatStatsFixed(item.ctr);
    },
    formatCTR2(item) {
      if (item.is_control) return "-";
      if (this.mode === "completed")
        return this.formatStatsFixed(percentage(item.stats.goals2, item.stats.impressions));
      return this.formatStatsFixed(item.ctr2);
    },
    completedConversionPct(item, secondary) {
      const numeratorKey = secondary
        ? this.hasCustomGoal
          ? "custom_goals_b2"
          : "goals2"
        : this.hasCustomGoal
          ? "custom_goals"
          : "goals";
      const denominatorKey = secondary
        ? "uimpressions"
        : item.is_control
          ? "uholdouts"
          : this.usersKey;
      return percentage(item.stats[numeratorKey] || 0, item.stats[denominatorKey] || 0);
    },
    getActiveCustomGoals(item) {
      const key = `custom_goals_${this.experimentsStore.customGoalInteraction}`;
      return item.is_control ? item.custom_goals_holdout : item[key];
    },
    // ── Active: "Use this" winner flow ──────────────────────────────────────
    confirmModal(item) {
      this.variantItem = item;
      this.$refs.confirmDialogRef.open();
    },
    closeConfirmDialog() {
      this.$refs.confirmDialogRef.close();
      this.variantItem = {};
    },
    async onWinner(winnerId, pause) {
      const winnerVariation = this.variations.find(item => item.id === winnerId);
      const appId = this.$route.params.aid;
      const pathId = this.model.id;
      await this.experimentsStore.runExperimentWithVariant({
        appId,
        pathId,
        expId: this.experiment.id,
        variation: winnerVariation,
      });
      const fetchResult = await this.promptStore.getPrompt({ appId, pathId });
      if (pause && fetchResult !== null) {
        const modelPath = cloneDeep(this.promptStore.prompt);
        modelPath.is_enabled = false;
        this.promptStore
          .updatePrompt({ appId, pathId, modelPath })
          .then(() => null)
          .catch(() => null);
      }
    },
    setPieVariant(item) {
      if (!item.is_control) this.$emit("update:pieActionGroupId", item.variant.id);
    },
  },
};
</script>

<style scoped>
.exp-var-name-href {
  min-width: 150px;
  display: inline-block;
}
</style>
